import {
  setCookie,
  saveState,
  getCookie,
  loadState,
  clearState,
  eraseCookie,
  DbetSingleton,
} from 'utils';
import { AUTH } from 'constants/popups';
import { LOADING, LOADED } from 'constants/data';
import { DBET_USER_DATA, GENERATED_IMAGE_SIZE } from 'constants/auth';

import { setError } from './error';
import { closePopup, closeAllPopups } from './popup';
import { setHideFrozenAmount } from './uiState';
import {
  SIGN_IN,
  SIGN_OUT,
  SET_USER_SETTING,
  SET_RESTORE_LOGIN,
  SET_CURRENT_LANGUAGE,
  SIGN_IN_LOADING_STATUS,
  SET_WALLET_NAME,
} from './actionTypes';

const dbet = DbetSingleton.getInstance();

export const signOut = () => ({
  type: SIGN_OUT,
});

export const setUserSetting = payload => ({
  type: SET_USER_SETTING,
  payload,
});

export const setWalletName = payload => ({
  type: SET_WALLET_NAME,
  payload,
});

export const setCurrentLanguage = payload => {
  saveState('lang', payload);

  return {
    type: SET_CURRENT_LANGUAGE,
    payload,
  };
};

export const setHasRestoreLogin = payload => ({
  type: SET_RESTORE_LOGIN,
  payload,
});

export const restoreLogin = userCredentials => async dispatch => {
  dispatch({
    type: SIGN_IN_LOADING_STATUS,
    payload: LOADING,
  });

  try {
    const { error, result, hasError, errorId } = await dbet.restoreLogin(userCredentials);

    if (hasError) {
      dispatch(setUserSetting({ ...result, loginFinished: true }));
      dispatch(setError({ errorId, error }));
      dispatch(logout());
    }

    const hasChannel = await dbet.hasChannel();
    const sessionData = loadState(DBET_USER_DATA, 'sessionStorage');

    if (result && !hasError) {
      const { address, username, channelAddress } = result;

      const isKYC = await dbet.isKYC();
      const isERC20 = await dbet.isErc20();

      const image = await dbet.generateImageFromAddress(address, 50);
      const balance = await dbet.accountBalance();
      const channelBalance = await dbet.channelBalance();

      const userSettings = {
        image,
        address,
        balance,
        username,
        hasChannel,
        channelBalance,
        channelAddress,
        loginFinished: true,
      };

      const walletName =
        dbet.walletName() || userSettings.walletName || sessionData.walletName || '';

      if (isERC20) {
        userSettings.nativeCurrencyBalance = await dbet.nativeCurrencyBalance(address);
      }

      if (isKYC) {
        userSettings.verificationStatus = await dbet.getApplicantStatus(address);
      }

      dispatch(setUserSetting(userSettings));
      dispatch(setWalletName(walletName));
      dispatch({ type: SIGN_IN });
    } else {
      console.error(error);
    }

    const checkSigners = loginData => {
      const { signer, signerPrivateKey, signerVerificationSign } = loginData;
      const { sessionSigner, sessionSignerPrivateKey, sessionSignerVerificationSign } = sessionData;

      if (
        !(signer === sessionSigner) ||
        !(signerPrivateKey === sessionSignerPrivateKey) ||
        !(signerVerificationSign === sessionSignerVerificationSign)
      ) {
        return false;
      }
      return true;
    };

    if (!checkSigners(result) && !hasError) {
      const { walletName } = loadState(DBET_USER_DATA, 'sessionStorage');
      clearState('sessionStorage');
      saveState(DBET_USER_DATA, { ...result, walletName }, 'sessionStorage');
      dispatch(setUserSetting({...result, walletName}));

    }
  } catch (error) {
    if (getCookie(DBET_USER_DATA)) {
      eraseCookie(DBET_USER_DATA);
    }

    if (loadState(DBET_USER_DATA, 'sessionStorage')) {
      clearState('sessionStorage');
    }
  } finally {
    dispatch({
      type: SIGN_IN_LOADING_STATUS,
      payload: LOADED,
    });
  }
};

export const signIn = (username, password, saveInStorage, isRegister) => async dispatch => {
  dispatch({
    type: SIGN_IN_LOADING_STATUS,
    payload: LOADING,
  });

  try {
    const { error, result, hasError } = await dbet.login(username, password);
    const hasChannel = await dbet.hasChannel();
    const balance = await dbet.accountBalance();
    const isKYC = await dbet.isKYC();
    let verificationStatus = 'resolved';

    if (isKYC && result?.address) {
      verificationStatus = await dbet.getApplicantStatus(result.address);
    } else {
      verificationStatus = 'not-verified';
    }

    if (
      Number(balance) <= 0 &&
      !hasChannel &&
      !isRegister &&
      result?.address &&
      verificationStatus !== 'resolved'
    ) {
      dispatch(setUserSetting({ loginFinished: true, hasChannel: false }));
      return false;
    }

    if (result && !hasError) {
      const { address, channelAddress } = result;
      const isERC20 = await dbet.isErc20();

      const image = await dbet.generateImageFromAddress(address, GENERATED_IMAGE_SIZE);
      const channelBalance = await dbet.channelBalance();

      const userSettings = {
        image,
        address,
        balance,
        username,
        hasChannel,
        channelBalance,
        channelAddress,
        loginFinished: true,
      };

      const walletName =
        dbet.walletName() ||
        userSettings.walletName ||
        loadState(DBET_USER_DATA, 'sessionStorage').walletName ||
        '';

      if (isERC20) {
        userSettings.nativeCurrencyBalance = await dbet.nativeCurrencyBalance(address);
      }

      if (isKYC) {
        userSettings.verificationStatus = verificationStatus;
      }

      dispatch(setUserSetting(userSettings));
      dispatch(setWalletName(walletName));
      dispatch({ type: SIGN_IN });

      if (saveInStorage) {
        setCookie(DBET_USER_DATA, result, 3);
      } else {
        saveState(DBET_USER_DATA, result, 'sessionStorage');
      }
      return true;
    } else {
      alert(error);
    }
  } finally {
    dispatch({
      type: SIGN_IN_LOADING_STATUS,
      payload: LOADED,
    });
  }
};

export const signInWithMetamask = (saveInStorage, walletConnect) => async dispatch => {
  dispatch({
    type: SIGN_IN_LOADING_STATUS,
    payload: LOADING,
  });

  try {
    const { result, hasError, errorId, error } = await dbet[
      walletConnect ? 'loginWithWalletConnect' : 'loginWithMetamask'
    ]();
    if (result && !hasError) {
      const { address, channelAddress } = result;
      const isKYC = await dbet.isKYC();
      const isERC20 = await dbet.isErc20();

      const balance = await dbet.accountBalance();
      const hasChannel = await dbet.hasChannel();
      const channelBalance = await dbet.channelBalance();
      const image = await dbet.generateImageFromAddress(address, 50);

      if (saveInStorage) {
        setCookie(DBET_USER_DATA, result, 3);
      } else {
        saveState(DBET_USER_DATA, result, 'sessionStorage');
      }

      const userSettings = {
        address,
        image,
        hasChannel,
        balance,
        channelBalance,
        channelAddress,
        loginFinished: true,
      };

      const walletName =
        dbet.walletName() ||
        userSettings.walletName ||
        loadState(DBET_USER_DATA, 'sessionStorage').walletName ||
        '';

      if (isERC20) {
        userSettings.nativeCurrencyBalance = await dbet.nativeCurrencyBalance(address);
      }

      if (isKYC) {
        userSettings.verificationStatus = await dbet.getApplicantStatus(address);
      }

      dispatch(setUserSetting(userSettings));
      dispatch(setWalletName(walletName));
      dispatch({ type: SIGN_IN });
      dispatch(closePopup(AUTH));
    } else {
      !['User closed modal', 'Connection request reset. Please try again.'].includes(error) &&
        dispatch(setError({ errorId, error }));
    }
  } finally {
    dispatch({
      type: SIGN_IN_LOADING_STATUS,
      payload: LOADED,
    });
  }
};

export const logout = () => async dispatch => {
  await dbet.logout();

  dispatch({
    type: SIGN_IN_LOADING_STATUS,
    payload: LOADED,
  });

  dispatch(closeAllPopups());
  dispatch(setHideFrozenAmount(false));

  if (getCookie(DBET_USER_DATA)) {
    eraseCookie(DBET_USER_DATA);
  }

  if (loadState(DBET_USER_DATA, 'sessionStorage')) {
    clearState('sessionStorage');
  }

  dispatch(signOut());
};
